/**
 * Ground and sky dressing for `/fly`'s scene, matched to the original.
 *
 * Every colour and every "is there one of these at all" decision below was
 * read off the running game under DOSBox-X rather than chosen
 * (`re-sessions/terrain/`, 2026-08-19 — Verdun, custom mission, clear sky;
 * captures `alt-low-*`, `alt5000-*`, `ref-a-*`). What that session found:
 *
 * - **The ground is one flat colour**, BARON.PAL index 143 `#4d5928`. A
 *   straight-down view at 4,710 ft (`shots/alt5000-down.png`) is 96% that
 *   single value, with only the placed `TERR` polygons on top of it.
 * - **The sky is one flat colour**, BARON.PAL index 48 `#7db6c3`, with a hard
 *   horizon line. There is no gradient and no horizon haze band. That is a
 *   *daytime clear-sky* measurement, and it is what `createSky()` still draws
 *   when called with no argument. `sun.c`'s own sky routine can paint a banded
 *   run instead, and the mission's time of day is what chooses one — see
 *   `time-of-day.ts` for the recovered mechanism, which of these colours are the
 *   original's, and the toggle that gates the rest.
 * - **There is no distance fog.** The same palette entries appear at every
 *   distance in every capture — a field 12 km out is byte-identical to one
 *   200 m out. This module used to fade both the ground and the scenery into
 *   a `#c0b394` haze, which is what made `/fly`'s terrain read as "sparse
 *   pale patches" (user report, 2026-08-19).
 * - **There is no survey grid.** That was ours, not the original's.
 *
 * The scenery *placed* on top of the ground is real — `flight/fly-region.ts`
 * loads the baked Verdun `.WLD` region via `three/world-instancer.ts`
 * (world-terrain.md §5's flat-plane verdict: there is no elevation data in
 * `.WLD`/`.TBL` to reconstruct, so a flat plane is correct, not a
 * placeholder) — the ground really has no per-position height, proven by
 * teleporting the player across 90 km of Verdun. What the flat plane does
 * NOT cover is relief carried by placed objects: `.GI` (gi-format.md §9)
 * gives Paris and Verdun ten conical hills of 727-1 905 m, 5-17 km across
 * (london-sea-placements.md §9.3 measured the shortest of the three shapes
 * in the original at 717-723 m, over a 4.5 km footprint),
 * which are the horizon at low altitude. Add those before calling the
 * terrain visually complete.
 *
 * Axis note: baked models are in the source's own frame — X span/right,
 * Y longitudinal/nose-positive, Z up (docs/formats/models-tbl.md §4.2) —
 * while the engine and three.js share Y-up with forward −Z. A single −90°
 * pitch about X converts one to the other: (x, y, z) → (x, z, −y). Also
 * exposed as pure math in `world/placement-transform.ts`'s `modelFrameToWorld`.
 */

import * as THREE from "three/webgpu";
import {
  MEASURED_GROUND_GREEN,
  MEASURED_SKY_RUN,
  skyBandCount,
  skyZenith,
  type PaletteColour,
  type SkyRun,
} from "./time-of-day.ts";

/** Rotation that takes a baked model's Z-up frame into the world's Y-up frame. */
export const MODEL_TO_WORLD_PITCH_RAD = -Math.PI / 2;

/**
 * Default ground-plane half-extent.
 *
 * With no fog to hide the plane's edge, the only thing that keeps the edge
 * off-screen is making the plane bigger than "however far the flight roams
 * from the world origin, plus the camera's far plane". Verdun is ~107 × 89 km
 * at the corrected placement scale (`world/wld-units.ts`), and `/fly`
 * recentres on St. Mihiel, so the aircraft can end up ~70 km out; 150 km of
 * half-extent leaves ≥80 km beyond that against a 26 km far plane
 * (`flight-view.ts`'s `CAMERA_FAR_M`). It is one four-vertex quad either way.
 */
export const GROUND_HALF_EXTENT_M = 150000;

/**
 * Sky-sphere radius. It only has to be smaller than the camera's far plane
 * (`flight-view.ts`'s `CAMERA_FAR_M` = 26 km) — a sphere clipped away entirely
 * leaves no sky at all — because the sphere is *recentred on the camera every
 * frame (`createSky`), so its size has nothing to do with how far the flight
 * roams. Depth ordering, not radius, is what keeps it behind the world: it
 * draws first (`renderOrder = -1`) and writes no depth, so every piece of
 * scenery drawn afterwards paints over it whatever their distances are.
 */
export const SKY_RADIUS_M = 20000;

/**
 * How far out a sky object is placed. Inside the dome's own radius so the two
 * can never swap over, and far enough that no scenery is ever between the
 * camera and it by accident — nothing else depends on the number, because a sky
 * object's size is solved back from it every frame
 * (`three/sprite-screen-size.ts`).
 *
 * It lives beside the radius rather than in `sky-dressing.ts` (which
 * re-exports it) so `star-field.ts` can share it without the two modules
 * importing each other.
 */
export const CELESTIAL_DISTANCE_M = SKY_RADIUS_M * 0.9;

/**
 * The run's palette entries as a texture the shader can step through — one
 * texel per entry, `NearestFilter`, `NoColorSpace`.
 *
 * The same device `three/ramp-material.ts` and `three/structure-ramp.ts` use to
 * walk `BARON.PAL` on the GPU, and for the same reason: a palette walk has to
 * land *on* an entry, and the only way to be sure a sampler never invents one
 * between two texels is to tell it not to filter. `NoColorSpace` because these
 * are raw game bytes and `ColorManagement` is off app-wide
 * (`three/webgpu-support.ts`), so a byte over 255 reaches the framebuffer
 * untouched.
 *
 * Exported so `test/sky-dome.test.ts` can pin the texel layout without a GPU:
 * with the recovered table in hand, the texture's *width* is the run's height
 * in the original's own scanlines, and that is a number worth asserting.
 */
export function skyRampTexture(run: SkyRun): THREE.DataTexture {
  // One texel per *scanline* when the run carries the recovered band table, so
  // an entry six scanlines tall gets six texels and one two scanlines tall gets
  // two. The thicknesses then fall out of nearest sampling instead of needing
  // arithmetic in the shader, and the texture is literally `ds:0x796` expanded.
  // Without a table every entry gets one texel, which is a uniform run.
  const widths = run.heights ?? run.colours.map(() => 1);
  const stops: PaletteColour[] = [];
  run.colours.forEach((colour, at) => {
    for (let scanline = 0; scanline < (widths[at] ?? 1); scanline += 1) stops.push(colour);
  });
  const texels = new Uint8Array(stops.length * 4);
  stops.forEach((colour, at) => {
    texels[at * 4] = (colour >> 16) & 0xff;
    texels[at * 4 + 1] = (colour >> 8) & 0xff;
    texels[at * 4 + 2] = colour & 0xff;
    texels[at * 4 + 3] = 255;
  });
  const texture = new THREE.DataTexture(texels, stops.length, 1, THREE.RGBAFormat);
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.generateMipmaps = false;
  texture.colorSpace = THREE.NoColorSpace;
  texture.needsUpdate = true;
  return texture;
}

/**
 * `setSkyShades`' banded run as a shader (`time-of-day.ts` for the recovered
 * mechanism and for which entries are the original's): the dome is cut into one
 * hard step per palette entry in the run, the steps bunched towards the skyline
 * by `run.concentration` so the sky a pilot actually looks at stays the colour
 * the original was measured at.
 *
 * **It reads the band's colour out of the run, it does not mix towards it**
 * (G18). The version this replaced quantised *where* a band fell and then still
 * ran `mix(zenith, horizon, step)` for the colour, so every interior band was a
 * blend of two palette entries — and the 48-63 run is not a straight line in
 * RGB, so those blends were colours `BARON.PAL` does not contain (`#81b6c2`,
 * `#84b6c1`). Indexing the ramp makes "every sky pixel is a palette entry" true
 * by construction rather than by intention.
 *
 * `positionGeometry.y / SKY_RADIUS_M` is the sine of the elevation without a
 * `normalize()`, because every vertex of this sphere is already at that radius.
 */
function bandedSkyMaterial(run: SkyRun): THREE.MeshBasicNodeMaterial {
  const material = new THREE.MeshBasicNodeMaterial({ side: THREE.BackSide, fog: false, depthWrite: false });
  // `positionGeometry.y / SKY_RADIUS_M` is the sine of the elevation without a
  // `normalize()`, because every vertex of this sphere is already at that radius.
  const sinElevation = THREE.TSL.positionGeometry.y.div(SKY_RADIUS_M).clamp(0, 1);
  const extentDeg = run.extentDeg;
  let u: THREE.Node<"float">;
  if (run.heights !== undefined && extentDeg !== undefined) {
    // **The recovered path (G9).** The band table is in scanlines measured from
    // the horizon, so the run is laid out linearly *in elevation* across
    // `extentDeg` and the texture's own texel widths carry the thicknesses.
    // `asin` rather than leaning on the small-angle approximation: it costs one
    // instruction and it keeps the layout honest if the extent is ever widened.
    const elevationDeg = sinElevation.asin().mul(180 / Math.PI);
    u = elevationDeg.div(extentDeg).oneMinus().clamp(0, 1);
  } else {
    // The three hours with no recovered table keep the port's own exponent, and
    // one texel per entry makes that a uniform run.
    const bands = skyBandCount(run);
    const towardsHorizon = THREE.TSL.pow(sinElevation.oneMinus(), run.concentration);
    // Texel centres, so the nearest sampler cannot be pushed off by one at a
    // boundary — `ramp-material.ts`'s `texelUv`, restated because that helper is
    // private to the model path.
    u = THREE.TSL.min(THREE.TSL.floor(towardsHorizon.mul(bands)), bands - 1).add(0.5).div(bands);
  }
  material.colorNode = THREE.TSL.texture(skyRampTexture(run), THREE.TSL.vec2(u, 0.5)).rgb;
  return material;
}

/**
 * An inverted sphere carrying the sky's palette run. A run of one band is a
 * flat `MeshBasicMaterial` — the measured original (module doc), and the
 * default — so a caller that wants the 1990 picture pays for no shader at all;
 * anything longer is `bandedSkyMaterial`. Either way it is one draw call, and
 * the scene is unlit either way too (the baked palette colours already carry
 * the original's shading).
 *
 * **It rides with the camera.** Left at the world origin the sphere encloses
 * only the first 20 km of a region that is 107 x 89 km, and `/fly` recentres on
 * St. Mihiel rather than on the map's middle — so an ordinary sortie flies out
 * through the shell and the sky turns inside out behind it (a `BackSide` sphere
 * seen from outside draws nothing at all where it should be the horizon). The
 * chase and fly-by cameras make it worse by sitting behind the aeroplane. Since
 * the sky is a flat colour with no gradient and no sun in it, a sphere centred
 * on the camera is indistinguishable from an infinitely distant one, and it can
 * never be escaped. `matrixAutoUpdate` stays on so the copied position takes
 * effect in the same frame's render.
 */
export function createSky(run: SkyRun = MEASURED_SKY_RUN): THREE.Mesh {
  const flat = skyBandCount(run) <= 1;
  return skyDome(
    flat
      ? new THREE.MeshBasicMaterial({ color: skyZenith(run), side: THREE.BackSide, fog: false, depthWrite: false })
      : bandedSkyMaterial(run),
  );
}

/**
 * The dome itself — the sphere, its render order and the per-frame recentring
 * `createSky`'s doc explains — with the material left to the caller.
 *
 * Exported so `enhanced-sky.ts` can hang its own material on the *same* dome
 * rather than building a second one: the port extension and the authentic path
 * then cannot drift on radius, tessellation, draw order or frustum handling,
 * which are the four things that decide whether there is a sky on screen at all.
 */
export function skyDome(material: THREE.Material): THREE.Mesh {
  const sky = new THREE.Mesh(new THREE.SphereGeometry(SKY_RADIUS_M, 32, 24), material);
  sky.renderOrder = -1;
  // Never cull it: its bounding sphere is recentred as the renderer draws it,
  // which is after the frustum test would have read the stale one.
  sky.frustumCulled = false;
  sky.onBeforeRender = (_renderer, _scene, camera) => {
    camera.getWorldPosition(sky.position);
    sky.updateMatrixWorld();
  };
  return sky;
}

/**
 * `halfExtentM` defaults to `GROUND_HALF_EXTENT_M` (`/fly`'s ground); the
 * `/assets` World viewer passes a per-region size instead. `colour` defaults to
 * the measured `#4d5928`; `time-of-day.ts` dims it for the twilight and night
 * treatments, since a noon-bright ground under a dark sky reads worse than
 * either alone.
 */
export function createGround(
  halfExtentM: number = GROUND_HALF_EXTENT_M,
  colour: PaletteColour = MEASURED_GROUND_GREEN,
): THREE.Group {
  const ground = new THREE.Group();
  const extent = halfExtentM * 2;

  const plane = new THREE.Mesh(new THREE.PlaneGeometry(extent, extent), new THREE.MeshBasicMaterial({ color: colour }));
  plane.rotation.x = -Math.PI / 2;
  // A few centimetres below datum: TERR models carry flat ground-level
  // polygons (fields, roads, lakes) at exactly y=0, which would be coplanar
  // with this plane and z-fight on every placed object (user report
  // 2026-08-19). The offset is invisible at any gameplay distance. What
  // actually makes 8 cm enough at range is the renderer's reversed-Z depth
  // buffer (`flight-view.ts`'s `REVERSED_DEPTH_BUFFER`); a conventional
  // buffer cannot resolve this gap past a few hundred metres.
  plane.position.y = -0.08;
  ground.add(plane);
  return ground;
}
